import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";

const WeatherWidget = () => {
  const [city, setCity] = useState(() => {
    return localStorage.getItem("whaticket-widget-city") || "Florianópolis";
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchWeather = useCallback((cityName) => {
    if (!cityName) return;
    setLoading(true);
    setError(null);

    // 1. Geocoding API to get lat/lon
    fetch(
      `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(
        cityName
      )}&count=1&language=pt`
    )
      .then((res) => res.json())
      .then((geoData) => {
        if (!geoData.results || geoData.results.length === 0) {
          throw new Error("Cidade não encontrada");
        }
        const location = geoData.results[0];
        const { latitude, longitude, name, admin1, country } = location;
        const displayName = `${name}${admin1 ? `, ${admin1}` : ""} - ${country}`;

        // 2. Weather forecast API
        return fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current_weather=true`
        )
          .then((res) => res.json())
          .then((weatherData) => {
            setWeather({
              city: displayName,
              temp: Math.round(weatherData.current_weather.temperature),
              wind: weatherData.current_weather.windspeed,
              code: weatherData.current_weather.weathercode,
            });
            localStorage.setItem("whaticket-widget-city", cityName);
            setLoading(false);
          });
      })
      .catch((err) => {
        setError(err.message || "Erro ao buscar clima");
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchWeather(city);
  }, [city, fetchWeather]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setCity(searchQuery.trim());
      setSearchQuery("");
    }
  };

  const getWeatherDescription = (code) => {
    if (code === 0) return "☀️ Céu limpo";
    if (code >= 1 && code <= 3) return "⛅ Parcialmente nublado";
    if (code >= 45 && code <= 48) return "🌫️ Nevoeiro";
    if (code >= 51 && code <= 67) return "🌧️ Chuva leve/moderada";
    if (code >= 80 && code <= 82) return "🌦️ Pancadas de chuva";
    if (code >= 95) return "⛈️ Tempestade";
    return "🌡️ Clima atual";
  };

  return (
    <Box style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <form onSubmit={handleSearch} style={{ display: "flex", marginBottom: 12 }}>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Buscar cidade..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          fullWidth
          style={{ backgroundColor: "#ffffff" }}
        />
        <IconButton type="submit" size="small" style={{ marginLeft: 4 }}>
          <SearchIcon />
        </IconButton>
      </form>

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexGrow={1}
        >
          <CircularProgress size={24} />
        </Box>
      ) : error ? (
        <Box textAlign="center" p={2} flexGrow={1}>
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        </Box>
      ) : weather ? (
        <Box
          textAlign="center"
          style={{
            backgroundColor: "#ffffff",
            borderRadius: 8,
            padding: 12,
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography variant="body2" style={{ fontWeight: "bold", color: "#555" }}>
            📍 {weather.city}
          </Typography>
          <Typography
            variant="h3"
            style={{ fontWeight: "bold", color: "#1976d2", margin: "8px 0" }}
          >
            {weather.temp}°C
          </Typography>
          <Typography variant="body2" style={{ fontWeight: "bold" }}>
            {getWeatherDescription(weather.code)}
          </Typography>
          <Typography variant="caption" style={{ color: "#777", marginTop: 4 }}>
            Vento: {weather.wind} km/h
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

export default WeatherWidget;
